/**
 * The tech's own backflow license, on his own screen (#1886, Corbin 10/10).
 *
 * Amber for the 30 days before it runs out; red once it has — after that the
 * server refuses to submit a test report signed with it. No bell: the warning
 * lives where the tech already looks (the iPad home, Review & Submit).
 * Helpers never sign, and a current or missing license shows nothing.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { IdCard } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { testerLicenseNoticeCopy, type MyTesterLicense } from "./tester-license-notice-copy";

export function useMyTesterLicense(enabled = true) {
    return useQuery({
        queryKey: ["me", "tester-license"],
        queryFn: () => api.get<{ testerLicense: MyTesterLicense }>("/api/me/tester-license"),
        enabled,
        staleTime: 5 * 60_000,
        retry: false,
    });
}

export function TesterLicenseNotice({ where, className }: { where: "home" | "submit"; className?: string }) {
    const q = useMyTesterLicense();
    const lic = q.data?.testerLicense;
    const copy = lic && typeof lic === "object" ? testerLicenseNoticeCopy(lic) : null;
    if (!copy) return null;
    const red = copy.tone === "red";
    return (
        <div
            role={red ? "alert" : "status"}
            data-testid={`tester-license-notice-${copy.tone}`}
            className={cn(
                "flex items-start gap-3 rounded-lg border-2 p-3",
                red
                    ? "border-red-500 bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-100"
                    : "border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100",
                className,
            )}
        >
            <div className={cn("shrink-0 rounded-full p-1.5", red ? "bg-red-500/20" : "bg-amber-500/20")}>
                <IdCard className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="min-w-0 space-y-0.5">
                <p className="text-sm font-semibold">{copy.headline}</p>
                <p className="text-sm">{copy.body}</p>
                {where === "home" && (
                    <Link to="/field/profile" className="inline-block text-sm font-medium underline">
                        My credentials
                    </Link>
                )}
            </div>
        </div>
    );
}
